import React, { ReactElement } from 'react';
import Image from 'next/image';
import Grid from '@components/grid';
import Button from '@components/ui/button';
import Text from '@components/ui/text';
import { ASSET_URL } from 'lib/constants';
import Container from '@components/ui/container';
import CMSLink from '@components/CMSLink';
import { useUI } from '@contexts/ui.context';
import useHasMounted from '@utils/use-has-mounted';

export default function SnippetsInfoImage(block: any): ReactElement {
  const { openModal, setModalView, setModalData } = useUI();
  const { variant, description, title, snippets, image, cta } = block;
  const { link } = cta;

  const hasDownloadableResource: boolean = link.resource?.hasDownload ?? false;
  const isDownloadWalled: boolean = link?.resource?.download?.isWalled ?? false;
  //@ts-ignore
  const downloadUrl: string = link?.resource?.download?.filename;
  //@ts-ignore
  const resourceId: string =
    hasDownloadableResource && snippets?.resource?.download?.id.toString();

  function handleDownload() {
    if (hasDownloadableResource && isDownloadWalled) {
      setModalView('DOWNLOAD_VIEW');
      setModalData({ resourceId: link.resource.id });
      return openModal();
    }
  }

  const hasMounted = useHasMounted();
  if (!hasMounted) return <div></div>;

  return (
    <Container>
      <Grid>
        <div className="col-span-full lg:col-span-6 border-t pt-4 md:pt-8 lg:pt-10 border-heading">
          {variant === 'description' && (
            <Text
              className="mb-6 text-black whitespace-pre-wrap"
              variant="body"
            >
              {description}
            </Text>
          )}

          {variant === 'snippet' && (
            <Text
              className="mb-6 lg:mb-8 sm:max-w-xs md:max-w-lg"
              variant="subHeading"
            >
              {title}
            </Text>
          )}

          {variant === 'snippet' &&
            snippets.map((snippet: any, i: number) => {
              return (
                <div
                  key={`snippet-description-map--${i}`}
                  className="flex flex-col sm:max-w-xs md:max-w-lg mb-6 lg:mb-8"
                >
                  <Text variant="heading" className="mb-1">
                    {snippet.title}
                  </Text>
                  <Text>{snippet.description}</Text>
                </div>
              );
            })}
        </div>

        <div className="group relative col-span-full lg:col-span-6 min-h-[280px] md:[min-h-300px] lg:min-h-[420px]">
          <div className="z-20 lg:border-t md:max-w-md lg:max-w-max xl:max-w-md lg:border-white p-8 lg:p-0 lg:pt-8 absolute inset-0  md:bottom-8 md:right-8 lg:bottom-auto lg:left-8 lg:right-8 lg:top-8 xl:left-auto xl:right-8 xl:top-auto xl:bottom-8 flex flex-col justify-start items-start">
            <Text variant="mediumHeading" className="text-white mb-4">
              {cta.title}
            </Text>
            {cta.description && (
              <Text className="text-white opacity-70">{cta.description}</Text>
            )}

            <CMSLink {...link}>
              <Button
                type="button"
                variant="cta"
                invert={true}
                onClick={link.resource ? handleDownload : undefined}
                className="mt-8 hover:border-transparent hover:bg-telesoft w-full border-white text-white"
              >
                {link.label}
              </Button>
            </CMSLink>
          </div>

          <div className="absolute inset-0 z-10 bg-gradient-to-t from-black to-transparent opacity-80" />
          <Image
            src={
              image?.filename
                ? ASSET_URL + '/' + image.sizes.thumbnail.filename
                : '/assets/images/products/flowprobe-placeholder.png'
            }
            className="object-cover object-center"
            alt={image?.alt || ''}
            loading="lazy"
            layout="fill"
          />
        </div>
      </Grid>
    </Container>
  );
}
